import React, { useRef, useState } from "react";

export default function PhoneInput({ value, onChange, name, ...props }) {
  const inputRef = useRef(null);
  const [isInvalid, setIsInvalid] = useState(false);

  // Получить только цифры из строки, максимум 11 (Россия +7 + 10 цифр)
  const getDigits = (str) => str.replace(/\D/g, "").slice(0, 11);

  // Форматирование номера
  const formatPhone = (digits) => {
    if (!digits.startsWith("7")) digits = "7" + digits.slice(1);
    let res = "+7";
    if (digits.length > 1) res += ` (${digits.slice(1, 4)}`;
    if (digits.length >= 4) res += `) ${digits.slice(4, 7)}`;
    if (digits.length >= 7) res += `-${digits.slice(7, 9)}`;
    if (digits.length >= 9) res += `-${digits.slice(9, 11)}`;
    return res;
  };

  // Находит индекс цифры (в digits), соответствующий позиции курсора в formattedValue
  const getDigitIndexByCursorPos = (formattedValue, cursorPos) => {
    let digitIndex = 0;
    for (let i = 0; i < cursorPos; i++) {
      if (/\d/.test(formattedValue[i])) digitIndex++;
    }
    return digitIndex;
  };

  // Возвращает позицию курсора в formattedValue для заданного индекса цифры
  const getCursorPosByDigitIndex = (formattedValue, digitIndex) => {
    let digitsCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        if (digitsCount === digitIndex) return i;
        digitsCount++;
      }
    }
    return formattedValue.length;
  };

  const handleChange = (e) => {
    let input = e.target.value;
    let digits = getDigits(input);

    // Если первая цифра после +7 не 9 — заменяем на 9
    if (digits.length > 1 && digits[1] !== "9") {
      digits = digits[0] + "9" + digits.slice(2);
    }

    digits = digits.slice(0, 11);

    const formatted = formatPhone(digits);

    onChange({
      target: {
        name,
        value: formatted,
      },
    });

    // Курсор в конец
    setTimeout(() => {
      const len = formatted.length;
      inputRef.current?.setSelectionRange(len, len);
    }, 0);

    setIsInvalid(digits.length < 11);
  };

  const handleKeyDown = (e) => {
    if (e.key !== "Backspace" && e.key !== "Delete") {
      // Разрешаем только цифры и специальные клавиши
      const allowedKeys = [
        "ArrowLeft",
        "ArrowRight",
        "Tab",
        "Home",
        "End",
        "Backspace",
        "Delete",
      ];
      if (!/[0-9]/.test(e.key) && !allowedKeys.includes(e.key)) {
        e.preventDefault();
      }
      return;
    }

    e.preventDefault(); // предотвращаем дефолтное удаление, т.к. делаем своё

    const input = inputRef.current;
    const selectionStart = input.selectionStart;
    const selectionEnd = input.selectionEnd;

    let digits = getDigits(value);

    // Индексы цифр в зависимости от позиции курсора
    const digitIndex = getDigitIndexByCursorPos(value, selectionStart);

    if (selectionStart !== selectionEnd) {
      // Если выделение — удаляем выделенные цифры
      let startDigitIndex = getDigitIndexByCursorPos(value, selectionStart);
      let endDigitIndex = getDigitIndexByCursorPos(value, selectionEnd);
      digits = digits.slice(0, startDigitIndex) + digits.slice(endDigitIndex);
      // Позиция курсора после удаления — на месте startDigitIndex
      const formatted = formatPhone(digits);
      onChange({ target: { name, value: formatted } });
      setTimeout(() => {
        const pos = getCursorPosByDigitIndex(formatted, startDigitIndex);
        inputRef.current?.setSelectionRange(pos, pos);
      }, 0);
      setIsInvalid(digits.length < 11);
      return;
    }

    // Если Backspace — удаляем цифру слева от курсора (digitIndex-1)
    if (e.key === "Backspace" && digitIndex > 1) {
      digits = digits.slice(0, digitIndex - 1) + digits.slice(digitIndex);
      const formatted = formatPhone(digits);
      onChange({ target: { name, value: formatted } });
      setTimeout(() => {
        const pos = getCursorPosByDigitIndex(formatted, digitIndex - 1);
        inputRef.current?.setSelectionRange(pos, pos);
      }, 0);
      setIsInvalid(digits.length < 11);
      return;
    }

    // Если Delete — удаляем цифру справа от курсора (digitIndex)
    if (e.key === "Delete" && digitIndex < digits.length) {
      digits = digits.slice(0, digitIndex) + digits.slice(digitIndex + 1);
      const formatted = formatPhone(digits);
      onChange({ target: { name, value: formatted } });
      setTimeout(() => {
        const pos = getCursorPosByDigitIndex(formatted, digitIndex);
        inputRef.current?.setSelectionRange(pos, pos);
      }, 0);
      setIsInvalid(digits.length < 11);
      return;
    }
  };

  const handleBlur = () => {
    setIsInvalid(getDigits(value).length < 11);
  };

  React.useEffect(() => {
    if (!value) {
      onChange({ target: { name, value: "+7" } });
    }
  }, [value, name, onChange]);

  return (
    <input
      {...props}
      ref={inputRef}
      type="text"
      inputMode="numeric"
      maxLength={18}
      autoComplete="tel"
      placeholder="+7 (___) ___-__-__"
      onChange={handleChange}
      onKeyDown={handleKeyDown}
      onBlur={handleBlur}
      className={`${props.className ?? ""} ${isInvalid ? "invalid" : ""}`}
      name={name}
      value={value}
    />
  );
}
